/**
 * Searchable single-select with type-ahead + optional grouping.
 * Purposefully minimal (no external combobox libs) — uses a native
 * details/summary popover so keyboard + accessibility come free.
 */
import { useMemo, useRef, useState, useEffect } from "react";
import { CaretDown, MagnifyingGlass, X } from "@phosphor-icons/react";

export default function SearchableSelect({
  value, options, onChange, placeholder = "Search…",
  groupBy = null, testId = "searchable-select",
}) {
  const [open, setOpen]   = useState(false);
  const [query, setQuery] = useState("");
  const ref = useRef(null);
  const inputRef = useRef(null);

  const selected = options.find((o) => o.value === value);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter((o) =>
      (o.label || "").toLowerCase().includes(q)
      || (o.hint  || "").toLowerCase().includes(q)
      || (o.meta  || "").toLowerCase().includes(q)
      || String(o.value || "").toLowerCase().includes(q)
    );
  }, [query, options]);

  const grouped = useMemo(() => {
    if (!groupBy) return null;
    const out = {};
    filtered.forEach((o) => {
      const g = o[groupBy] || "Other";
      (out[g] = out[g] || []).push(o);
    });
    return out;
  }, [filtered, groupBy]);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const onClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 20);
  }, [open]);

  return (
    <div ref={ref} className="relative" data-testid={testId}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid={`${testId}-button`}
        className={`w-full h-11 flex items-center gap-2 rounded-md border ${open ? "border-brand-blue ring-1 ring-brand-blue/20" : "border-slate-200"} bg-white px-3 text-[14px] text-left`}>
        <span className="flex-1 min-w-0 truncate">
          {selected ? (
            <>
              <span className="text-slate-900">{selected.label}</span>
              {selected.hint && <span className="text-slate-400 text-[11.5px] ml-2 font-mono">{selected.hint}</span>}
            </>
          ) : (
            <span className="text-slate-400">{placeholder}</span>
          )}
        </span>
        <CaretDown size={12} className="text-slate-400" />
      </button>

      {open && (
        <div className="absolute left-0 right-0 mt-1 rounded-md border border-slate-200 bg-white shadow-lg z-40 overflow-hidden"
                data-testid={`${testId}-popover`}>
          <div className="flex items-center gap-2 px-3 border-b border-slate-100 h-10">
            <MagnifyingGlass size={13} className="text-slate-400" />
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={placeholder}
              data-testid={`${testId}-search`}
              className="flex-1 bg-transparent outline-none text-[13px] placeholder:text-slate-400"
            />
            {query && (
              <button type="button" onClick={() => setQuery("")}
                        data-testid={`${testId}-clear`}
                        className="w-6 h-6 rounded hover:bg-slate-100 flex items-center justify-center">
                <X size={11} className="text-slate-400" />
              </button>
            )}
          </div>
          <div className="max-h-72 overflow-y-auto py-1" data-testid={`${testId}-list`}>
            {filtered.length === 0 && (
              <div className="px-4 py-6 text-[12px] text-slate-500 text-center">No matches.</div>
            )}
            {grouped
              ? Object.entries(grouped).map(([g, opts]) => (
                <div key={g}>
                  <div className="px-3 py-1 text-[9.5px] uppercase tracking-[0.16em] text-slate-500 bg-slate-50/70">
                    {g}
                  </div>
                  {opts.map((o) => (
                    <OptionRow key={o.value} option={o} selected={o.value === value}
                                  onPick={(v) => { onChange(v); setOpen(false); setQuery(""); }} />
                  ))}
                </div>
              ))
              : filtered.map((o) => (
                <OptionRow key={o.value} option={o} selected={o.value === value}
                              onPick={(v) => { onChange(v); setOpen(false); setQuery(""); }} />
              ))}
          </div>
        </div>
      )}
    </div>
  );
}

function OptionRow({ option, selected, onPick }) {
  return (
    <button
      type="button"
      onClick={() => onPick(option.value)}
      data-testid={`option-${option.value}`}
      className={`w-full flex items-center gap-3 px-3 py-1.5 text-left text-[13px] hover:bg-brand-blue/5
                     ${selected ? "bg-brand-blue/10" : ""}`}>
      <span className="flex-1 truncate">
        <span className={selected ? "font-semibold text-brand-blue" : "text-slate-800"}>{option.label}</span>
      </span>
      {option.hint && <span className="text-[11px] font-mono text-slate-400 shrink-0">{option.hint}</span>}
    </button>
  );
}
